import { FC, useEffect } from 'react';
import * as DOMPurify from 'isomorphic-dompurify';
import styles from './Pop.module.scss';
import ModalContainer from '#components/ui/ModalContainer';
import PopbImage from '#images/pops/popb.png';
import PopcImage from '#images/pops/popc.png';
import Button from '#components/ui/Button';
import { popup } from '#store/popup';
import { useStore } from '@nanostores/react';
import useResponsive from '#hooks/useResponsive';
import CloseIcon from '#images/pops/close.svg';
import analytics from '#utils/analytics';
import i18next from 'i18next';

type Props = {
  type: 'popb' | 'popc';
  popupContent: {
    popb: Record<string, string>;
    popc: Record<string, string>;
  };
};

const Pop: FC<Props> = ({ type, popupContent }: Props) => {
  const $popup = useStore(popup);
  const isMobile = useResponsive('mobile');
  const isPopb = type === 'popb';
  const lang = i18next.language;

  let oldScrollY = 0;
  let isUp = false;
  let startUpPosition = 0;

  const openPopup = () => {
    if (!sessionStorage.getItem(type)) {
      popup.set(type);
      sessionStorage.setItem(type, 'true');
    }
  };

  const handleScroll = () => {
    if (!isUp && window.scrollY < oldScrollY) {
      isUp = true;
      startUpPosition = window.scrollY;
    }

    oldScrollY = window.scrollY;

    if (isUp && startUpPosition - window.scrollY > 200) {
      openPopup();
    }
  };

  const handleMoveOut = (event: MouseEvent) => {
    if (event.clientY <= 0) {
      openPopup();
    }
  };

  const handleCloseClick = () => {
    analytics.event({ action: 'exit_close' });
    popup.set('');
  };

  useEffect(() => {
    if (isMobile) {
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      document.addEventListener('mouseleave', handleMoveOut);

      return () => {
        document.removeEventListener('mouseleave', handleMoveOut);
      };
    }
  }, [isMobile]);

  return (
    <ModalContainer show={$popup === type}>
      <div className={`${styles.container} ${!isPopb && styles.popc}`}>
        <img
          onClick={handleCloseClick}
          className={styles.close}
          src={CloseIcon.src}
          alt="close"
          width={19}
          height={19}
        />
        <div className={styles.content}>
          <div className={styles.block}>
            <p className={styles.title}>{popupContent[type].title}</p>
            <p
              className={styles.subTitle}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(popupContent[type].subTitle || ''),
              }}
            />
            <p className={styles.description1}>{popupContent[type].description1}</p>
          </div>

          <img
            className={styles.image}
            src={isPopb ? PopbImage.src : PopcImage.src}
            alt="wait"
            width={755}
            height={470}
          />
          <div className={styles.gradient} />

          <div className={styles.block}>
            <Button
              eventName="exit_click"
              href="email"
              lang={lang}
              text={
                isMobile && isPopb ? popupContent[type].buttonMobile : popupContent[type].button
              }
              customStyle="leavePage"
              currentFeature="isMainPage"
            />
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(popupContent[type].description2 || ''),
              }}
              className={styles.description2}
            />
          </div>
        </div>
      </div>
    </ModalContainer>
  );
};

export default Pop;
